import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Dropdown, Input, InputRef, Menu, Modal, Row } from 'antd';
import {
    TbArrowRight,
    TbMail,
    TbShieldCheck,
    TbUserHexagon,
    TbUserPentagon,
    TbUserSquare,
    TbUserX,
    TbX,
} from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useCASL } from '@/hooks/useCASL';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceResources } from '@/hooks/useWorkspaceResources';
import { EmptySection } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { PlatformForm as Form, PlatformSelect } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { PERMISSIONS } from '@/shared/permissions';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { useRoleStore } from '@/stores/database/entities/00-RBAC/04-Roles';
import { useTeamStore } from '@/stores/database/entities/06-Teams';
import { MemberPermissionType, MemberStatusType, SetStateMethod } from '@/types';
import { centerComponentInMiddleofView, checkForValidEmailAddress } from '@/utilities';

interface InviteToWorkspaceModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const InviteToWorkspaceModal: FC<InviteToWorkspaceModalProps> = ({ open, setOpen }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const [input, setInput] = useState<string>('');
    const [permission, setPermission] = useState<MemberPermissionType>(MemberPermissionType.MEMBER);
    const [customRoleId, setCustomRoleId] = useState<string | undefined>();
    const [teamId, setTeamId] = useState<string | undefined>();
    const [permissionDropdownOpen, setPermissionDropdownOpen] = useState(false);

    const { planTier } = useEntitlements();
    const { createInvitation } = useInvitationStore();
    const { fetchRoles, roles } = useRoleStore();
    const { fetchTeams, loading: teamsLoading, teams } = useTeamStore();
    const [resourcesCount] = useWorkspaceResources();
    const navigate = useNavigate();
    const location = useLocation();

    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (open) {
            void fetchRoles();
            void fetchTeams();
        }
    }, [fetchRoles, fetchTeams, open]);

    useEffect(() => {
        setPermissionDropdownOpen(false);
    }, [location.pathname, open]);

    const closeModal = () => {
        setPermissionDropdownOpen(false);
        setTeamId(undefined);
        setOpen(false);
    };

    const handleInviteMember = async () => {
        try {
            setLoading(true);
            await createInvitation({ emailAddress: input, permission, customRoleId, teamId });
            closeModal();
            setInput('');
            setPermission(MemberPermissionType.MEMBER);
            setCustomRoleId(undefined);
        } catch {
            toast.error('Unable to Send Invitation');
        } finally {
            setLoading(false);
        }

        //     try {
        //         setLoading(true);

        //         await authorizedAxiosInstance
        //             .post(`/api/workspaces/${workspace?.id}/members`, {
        //                 emailAddress: input,
        //                 permission,
        //                 fromAdminFirstName: user?.firstName,
        //                 fromAdminLastName: user?.lastName,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res?.data?.error) {
        //                     toast('Member Already in Workspace', {
        //                         icon: <TbUserCheck size='1rem' />,
        //                         description: res?.data?.error,
        //                     });
        //                 } else if (res?.data?.message) {
        //                     toast(`${res?.data?.message}`, {
        //                         icon: <TbUserCheck size='1rem' />,
        //                         description: res?.data?.emailAddress,
        //                     });
        //                 } else {
        //                     if (res.status === 200) {
        //                         toast('Invitation Sent', {
        //                             icon: <TbMailFast size='1rem' />,
        //                             description: `${res?.data['user']?.emailAddress}`,
        //                         });

        //                         setOpen(false);

        //                         setMembers([...members, res?.data]);

        //                         setInput('');

        //                         setLoading(false);
        //                     }
        //                 }
        //             })
        //             .finally(() => setLoading(false));
        //     } catch (error) {
        //         captureException(error);
        //     }
    };

    const permissions = [
        {
            key: MemberPermissionType.GUEST,
            icon: <TbUserSquare size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.5rem' }} />,
            label: 'Guest',
            description: 'Can view select workspace resources.',
        },
        {
            key: MemberPermissionType.MEMBER,
            icon: <TbUserHexagon size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.5rem' }} />,
            label: 'Member',
            description: `Can edit workspace resources they're invited to.`,
        },
        {
            key: MemberPermissionType.ADMIN,
            icon: <TbUserPentagon size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.5rem' }} />,
            label: 'Admin',
            description: `Full visibility and access to the workspace.`,
        },
    ] as any;

    const customRoleOptions = useMemo(
        () =>
            roles.map((role) => ({
                key: `custom:${role.id}`,
                role,
                icon: <TbShieldCheck size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.5rem' }} />,
                label: role.name,
                description: `${role.permissions?.length ?? 0} custom ${
                    role.permissions?.length === 1 ? 'policy' : 'policies'
                }`,
            })),
        [roles],
    );

    const teamOptions = useMemo(
        () =>
            (teamsLoading ? [] : teams)
                .filter((team) => !team.archived)
                .map((team) => ({ label: team.name, value: team.id }))
                .sort((left, right) => left.label.localeCompare(right.label)),
        [teams, teamsLoading],
    );

    const selectedCustomRole = customRoleId ? roles.find((role) => role.id === customRoleId) : undefined;
    const selectedPermissionKey = customRoleId ? `custom:${customRoleId}` : permission;
    const selectedPermissionLabel = selectedCustomRole?.name ?? permission;
    const selectedPermissionIcon =
        [...permissions, ...customRoleOptions].find((option) => option.key === selectedPermissionKey)?.icon ??
        (selectedPermissionKey === MemberStatusType.BLOCKED ? (
            <TbUserX size='1rem' style={{ verticalAlign: '-0.2rem', marginRight: '0.325rem' }} />
        ) : null);

    // Autofocus
    const titleInputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => titleInputRef.current?.focus(), 125);
        }
    }, [open]);

    const { can } = useCASL();

    const isEmailValid = checkForValidEmailAddress(input);
    const isFreePlan = planTier === 'free';
    const hasSeatAvailable = resourcesCount.members < 2; // adjust if needed

    // !IMPORTANT: It might be worthwhile to do to show a amount that will be charged/added onto the invoice in the event
    // That a workspace owner/admin invites

    const canInvite = can(PERMISSIONS.WORKSPACE.INVITE_USER) && isEmailValid && (!isFreePlan || hasSeatAvailable);
    const disabled = !canInvite;

    const inviteDisabledReason = !can(PERMISSIONS.WORKSPACE.INVITE_USER)
        ? 'Only workspace owners & administrators can invite members to the workspace.'
        : !isEmailValid
          ? 'Please enter a valid email address.'
          : isFreePlan && !hasSeatAvailable
            ? 'Upgrade your plan to add more members.'
            : null;

    return (
        <Modal
            destroyOnHidden
            mask={false}
            title='Invite to Workspace'
            open={open}
            closable
            closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
            onOk={closeModal}
            onCancel={closeModal}
            style={{ minWidth: '30vw' }}
            footer={
                <Row style={{}}>
                    <Col span={12} style={{ textAlign: 'left' }}>
                        <Button onClick={closeModal} disabled={loading}>
                            Cancel
                        </Button>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Button
                            loading={loading}
                            type='primary'
                            onClick={handleInviteMember}
                            disabled={loading || disabled}
                        >
                            Send
                            <TbArrowRight style={{ verticalAlign: '-0.15rem' }} />
                        </Button>
                    </Col>
                </Row>
            }
        >
            {inviteDisabledReason !== null && (
                <PlatformAlert
                    message={<>{inviteDisabledReason}</>}
                    status='warning'
                    styles={{ marginBottom: '0.5rem' }}
                />
            )}
            {/* <ActiveAndPurchasedSeatCountProgress /> */}
            <Form layout='vertical'>
                <Form.Item label='Email Address' style={{ marginBottom: '0.5rem' }}>
                    <Row gutter={[8, 8]}>
                        <Col flex='auto'>
                            <Input
                                ref={titleInputRef}
                                value={input}
                                onChange={(e: any) => {
                                    setInput(e.target.value);
                                }}
                                prefix={
                                    <TbMail style={{ marginRight: '0.375rem', color: token.colorTextQuaternary }} />
                                }
                                placeholder='Email Address...'
                                onPressEnter={(e: any) => {
                                    if (checkForValidEmailAddress(input) && !disabled) {
                                        // if (e.metaKey || e.ctrlKey) {
                                        handleInviteMember();

                                        e.target.blur();

                                        // }
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                </Form.Item>
                <Form.Item
                    label='Permission'
                    style={{ marginBottom: teamsLoading || teamOptions.length ? '0.5rem' : 0 }}
                >
                    <Row gutter={[8, 8]}>
                        <Col flex='auto'>
                            <Dropdown
                                destroyOnHidden
                                open={permissionDropdownOpen}
                                onOpenChange={setPermissionDropdownOpen}
                                trigger={['click']}
                                placement='bottomRight'
                                popupRender={() => (
                                    <DropdownMenuPanel
                                        style={{
                                            marginTop: '0.25rem',
                                            maxHeight: 'none',
                                            overflowY: 'visible',
                                        }}
                                        selectedKeys={[selectedPermissionKey]}
                                        onClick={(e: any) => {
                                            setPermissionDropdownOpen(false);

                                            if (e.key.startsWith('custom:') && e.key !== 'custom:none') {
                                                setPermission(MemberPermissionType.CUSTOM);
                                                setCustomRoleId(e.key.replace('custom:', ''));

                                                return;
                                            }

                                            setPermission(e.key);
                                            setCustomRoleId(undefined);
                                        }}
                                    >
                                        <div style={{ color: token.colorTextTertiary }}>
                                            <small style={{ marginLeft: '0.75rem', verticalAlign: '3px' }}>
                                                Permissions
                                            </small>
                                        </div>
                                        {/* Auto-assigns keys to the top-level item */}
                                        {/* {Children.toArray( */}
                                        {permissions.map((permission: any) => (
                                            <Menu.Item key={permission?.key}>
                                                {permission?.icon}
                                                {permission?.label}
                                                <br />
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    {permission?.description}
                                                </small>
                                            </Menu.Item>
                                        ))}
                                        <Menu.Divider />
                                        <div style={{ color: token.colorTextTertiary }}>
                                            <small style={{ marginLeft: '0.75rem', verticalAlign: '3px' }}>
                                                Custom Roles
                                            </small>
                                        </div>
                                        {customRoleOptions.length > 0 ? (
                                            customRoleOptions.map((customRole) => (
                                                <Menu.Item key={customRole.key}>
                                                    {customRole.icon}
                                                    {customRole.label}
                                                    <br />
                                                    <small style={{ color: token.colorTextTertiary }}>
                                                        {customRole.description}
                                                    </small>
                                                </Menu.Item>
                                            ))
                                        ) : (
                                            <div
                                                style={{
                                                    padding: '0.75rem 0',
                                                    ...centerComponentInMiddleofView,
                                                }}
                                            >
                                                <EmptySection
                                                    height='12rem'
                                                    icon={
                                                        <TbShieldCheck
                                                            size='50%'
                                                            style={{
                                                                verticalAlign: '-0.175rem',
                                                                color: token.colorTextTertiary,
                                                            }}
                                                        />
                                                    }
                                                    label={<>No Custom Roles</>}
                                                    description={<>Create roles from workspace permissions.</>}
                                                    extra={
                                                        <Button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                closeModal();
                                                                navigate('/settings/workspace/permissions/roles');
                                                            }}
                                                        >
                                                            Manage Custom Roles
                                                            <TbArrowRight />
                                                        </Button>
                                                    }
                                                />
                                            </div>
                                        )}
                                        <Menu.Divider />
                                        <Menu.Item key={MemberStatusType.BLOCKED} danger>
                                            <TbUserX
                                                size='1rem'
                                                style={{ verticalAlign: '-0.2rem', marginRight: '0.325rem' }}
                                            />
                                            Block User
                                            <br />
                                            <small style={{ color: token.colorTextTertiary }}>
                                                Prevents a user from being able to join this workspace.
                                            </small>
                                        </Menu.Item>
                                        {/* )} */}
                                    </DropdownMenuPanel>
                                )}
                            >
                                <DropdownButton block type='dashed' style={{ justifyContent: 'flex-start' }}>
                                    <Row style={{ width: '100%' }}>
                                        <Col flex='auto' style={{ textAlign: 'left' }}>
                                            {selectedPermissionIcon}
                                            <span>{selectedPermissionLabel}</span>
                                        </Col>
                                    </Row>
                                </DropdownButton>
                            </Dropdown>
                        </Col>
                    </Row>
                </Form.Item>
                {(teamsLoading || teamOptions.length > 0) && (
                    <Form.Item label='Team (Optional)' style={{ marginBottom: 0 }}>
                        <PlatformSelect
                            allowClear
                            loading={teamsLoading}
                            onChange={(value: string | undefined) => setTeamId(value)}
                            optionFilterProp='label'
                            options={teamOptions}
                            placeholder='Select A Team...'
                            showSearch
                            style={{ width: '100%' }}
                            value={teamId}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default InviteToWorkspaceModal;
